import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { executionsAPI } from '@/shared/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@travellegacy/nexops-design-system';
import { formatRelativeTime, getStatusColor } from '@/shared/lib/utils';
import { Clock } from 'lucide-react';
import type { Execution } from '@/shared/types';
import { Badge } from '@travellegacy/nexops-design-system';

export function RecentExecutions() {
  const { data, isLoading } = useQuery({
    queryKey: ['executions', 'recent'],
    queryFn: async () => {
      const response = await executionsAPI.recent(10);
      return response.data.data as Execution[];
    },
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Clock className="mr-2 h-5 w-5" />
            Execuções Recentes
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="h-16 bg-muted rounded" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center">
            <Clock className="mr-2 h-5 w-5" />
            Execuções Recentes
          </span>
          <Link to="/executions" className="text-sm font-normal text-primary hover:text-primary/80">
            Ver todas →
          </Link>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!data || data.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">Nenhuma execução encontrada</div>
        ) : (
          <div className="space-y-3">
            {data.map((execution) => (
              <Link
                key={execution.id}
                to={`/executions/${execution.id}`}
                className="block border rounded-lg p-4 hover:bg-accent transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <h4 className="font-medium text-card-foreground">
                        {execution.automation?.name || 'Automação'}
                      </h4>
                      <Badge className={getStatusColor(execution.status)}>{execution.status}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {formatRelativeTime(execution.startedAt)}
                    </p>
                  </div>
                  {execution.duration && (
                    <div className="text-sm text-muted-foreground">
                      {(execution.duration / 1000).toFixed(1)}s
                    </div>
                  )}
                </div>
                {execution.error && (
                  <p className="text-sm text-red-600 dark:text-red-400 mt-2 truncate">
                    {execution.error}
                  </p>
                )}
              </Link>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
